import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SelectField from '../inputs/SelectField';
import {
  FANCY_COLOR_INTENSITY_LIST,
  FANCY_COLOR_HUE_LIST,
  FANCY_COLOR_OVERTONE_LIST,
} from '../../constants/appConstants';

const DIAMOND_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'sold', label: 'Sold' },
  { value: 'onTheWay', label: 'On the Way' },
  { value: 'notForSell', label: 'Not for sell' },
  { value: 'deleted', label: 'Deleted' },
];

const DIAMOND_GRADE_OPTIONS = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];
const DIAMOND_TYPE_OPTIONS = ['Natural', 'CVD', 'HPHT'];
const LAB_OPTIONS = ['GIA', 'IGI', 'HDR'];

const EMPTY_FORM = {
  certificateNumber: '',
  sku: '',
  shipmentId: '',
  shipmentItemID: '',
  shape: '',
  carat: '',
  length: '',
  width: '',
  height: '',
  color: '',
  fancyIntensity: '',
  fancyHue: '',
  fancyOvertone: '',
  clarity: '',
  cut: '',
  polish: '',
  symmetry: '',
  lab: '',
  diamondType: '',
  basePrice: '',
  buyPricePerCarat: '',
  status: 'available',
};

/**
 * Add certified diamond dialog with local form state so typing does not re-render the inventory page.
 */
export default function AddDiamondDialog({
  open,
  onClose,
  onSaved,
  formShapeList = [],
  filterOptions = {},
  isInternal = false,
  canSeeBuyPrices = false,
  isMobile = false,
  colorCategory = 'standard',
}) {
  const { t } = useTranslation();
  const isFancy = colorCategory === 'fancy';
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError('');
  }, [open]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const shapeSelectOptions = useMemo(
    () => formShapeList.map((s) => {
      const shape = String(s).toUpperCase();
      return { value: shape, label: shape };
    }),
    [formShapeList],
  );

  const intensityOptions = useMemo(() => {
    const fromApi = filterOptions.fancyIntensities || [];
    const list = fromApi.length > 0 ? fromApi : FANCY_COLOR_INTENSITY_LIST;
    return list.map((v) => ({ value: v, label: v }));
  }, [filterOptions.fancyIntensities]);

  const hueOptions = useMemo(() => {
    const fromApi = filterOptions.fancyHues || [];
    const list = fromApi.length > 0 ? fromApi : FANCY_COLOR_HUE_LIST;
    return list.map((v) => ({ value: v, label: v }));
  }, [filterOptions.fancyHues]);

  const overtoneOptions = useMemo(() => {
    const fromApi = filterOptions.fancyOvertones || [];
    const list = fromApi.length > 0 ? fromApi : FANCY_COLOR_OVERTONE_LIST;
    return list.map((v) => ({ value: v, label: v }));
  }, [filterOptions.fancyOvertones]);

  const handleAdd = async () => {
    if (loading) return;
    const fancyOk = isFancy && form.fancyIntensity && form.fancyHue;
    const standardOk = !isFancy && form.color;
    if (!form.certificateNumber?.trim() || !form.shape || !form.carat || (!standardOk && !fancyOk) || !form.clarity || !form.lab?.trim() || form.basePrice === '') {
      setError('Certificate #, shape, carat, color, clarity, lab, and base price are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        colorCategory: isFancy ? 'fancy' : 'standard',
        certificateNumber: form.certificateNumber.trim(),
        sku: form.sku?.trim() || undefined,
        shipmentId: form.shipmentId?.trim() || undefined,
        shipmentItemID: form.shipmentItemID?.trim() || undefined,
        shape: (form.shape || '').trim().toUpperCase(),
        carat: Number(form.carat),
        length: form.length !== '' ? Number(form.length) : undefined,
        width: form.width !== '' ? Number(form.width) : undefined,
        height: form.height !== '' ? Number(form.height) : undefined,
        clarity: form.clarity,
        cut: form.cut || undefined,
        polish: form.polish || undefined,
        symmetry: form.symmetry || undefined,
        lab: form.lab.trim(),
        diamondType: form.diamondType || undefined,
        basePrice: Number(form.basePrice),
        ...(canSeeBuyPrices && form.buyPricePerCarat !== ''
          ? { buyPricePerCarat: Number(form.buyPricePerCarat) }
          : {}),
        status: form.status || 'available',
      };
      if (isFancy) {
        payload.fancyColor = {
          intensity: form.fancyIntensity,
          hue: form.fancyHue,
          ...(form.fancyOvertone ? { overtone: form.fancyOvertone } : {}),
        };
      } else {
        payload.color = form.color;
      }
      await axios.post('/api/diamonds', payload);
      onClose();
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to add diamond');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{isFancy ? t('inventory.addFancyDiamondTitle') : t('inventory.addDiamondTitle')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Certificate #" value={form.certificateNumber} onChange={(e) => setField('certificateNumber', e.target.value)} required fullWidth size="small" />
          <TextField label="SKU (optional)" value={form.sku} onChange={(e) => setField('sku', e.target.value)} fullWidth size="small" />
          {isInternal && <TextField label="Shipment ID" value={form.shipmentId} onChange={(e) => setField('shipmentId', e.target.value)} fullWidth size="small" />}
          {isInternal && <TextField label="Shipment Item ID" value={form.shipmentItemID} onChange={(e) => setField('shipmentItemID', e.target.value)} fullWidth size="small" />}
          <Box display="flex" gap={1}>
            <SelectField
              label="Shape"
              size="small"
              sx={{ flex: 1 }}
              value={form.shape}
              onChange={(next) => setField('shape', next)}
              options={shapeSelectOptions}
              emptyLabel="Select..."
              includeEmptyOption
            />
            <TextField label="Carat" type="number" value={form.carat} onChange={(e) => setField('carat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
            <SelectField
              label="Type"
              size="small"
              sx={{ flex: 1 }}
              value={form.diamondType}
              onChange={(next) => setField('diamondType', next)}
              options={DIAMOND_TYPE_OPTIONS.map((type) => ({ value: type, label: type }))}
              emptyLabel="—"
              includeEmptyOption
            />
          </Box>
          {isFancy ? (
            <>
              <SelectField
                label={t('inventory.fancyColorIntensity')}
                size="small"
                fullWidth
                value={form.fancyIntensity}
                onChange={(next) => setField('fancyIntensity', next)}
                options={intensityOptions}
                emptyLabel=""
              />
              <Box display="flex" gap={1}>
                <SelectField
                  label={t('inventory.fancyColorHue')}
                  size="small"
                  sx={{ flex: 1 }}
                  value={form.fancyHue}
                  onChange={(next) => setField('fancyHue', next)}
                  options={hueOptions}
                  emptyLabel=""
                />
                <SelectField
                  label={t('inventory.fancyColorOvertone')}
                  size="small"
                  sx={{ flex: 1 }}
                  value={form.fancyOvertone}
                  onChange={(next) => setField('fancyOvertone', next)}
                  options={overtoneOptions}
                  emptyLabel="—"
                  includeEmptyOption
                />
              </Box>
            </>
          ) : (
            <Box display="flex" gap={1}>
              <SelectField
                label="Color"
                size="small"
                sx={{ flex: 1 }}
                value={form.color}
                onChange={(next) => setField('color', next)}
                options={(filterOptions.colors || []).map((c) => ({ value: c, label: c }))}
                emptyLabel=""
              />
            </Box>
          )}
          <Box display="flex" gap={1}>
            <SelectField
              label="Clarity"
              size="small"
              sx={{ flex: 1 }}
              value={form.clarity}
              onChange={(next) => setField('clarity', next)}
              options={(filterOptions.clarities || []).map((c) => ({ value: c, label: c }))}
              emptyLabel=""
            />
            <SelectField
              label="Lab"
              size="small"
              sx={{ flex: 1 }}
              value={form.lab || ''}
              onChange={(next) => setField('lab', next)}
              options={LAB_OPTIONS.map((lab) => ({ value: lab, label: lab }))}
              emptyLabel=""
            />
          </Box>
          <Box display="flex" gap={1}>
            <SelectField
              label="Cut"
              size="small"
              sx={{ flex: 1 }}
              value={form.cut}
              onChange={(next) => setField('cut', next)}
              options={(filterOptions.cuts || []).map((c) => ({ value: c, label: c }))}
              emptyLabel=""
            />
            <SelectField
              label="Symmetry"
              size="small"
              sx={{ flex: 1 }}
              value={form.symmetry}
              onChange={(next) => setField('symmetry', next)}
              options={DIAMOND_GRADE_OPTIONS.map((s) => ({ value: s, label: s }))}
              emptyLabel="—"
              includeEmptyOption
            />
            <SelectField
              label="Polish"
              size="small"
              sx={{ flex: 1 }}
              value={form.polish}
              onChange={(next) => setField('polish', next)}
              options={DIAMOND_GRADE_OPTIONS.map((p) => ({ value: p, label: p }))}
              emptyLabel="—"
              includeEmptyOption
            />
          </Box>
          <Box display="flex" gap={1}>
            <TextField label="Length (mm)" type="number" value={form.length} onChange={(e) => setField('length', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
            <TextField label="Width (mm)" type="number" value={form.width} onChange={(e) => setField('width', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
            <TextField label="Height (mm)" type="number" value={form.height} onChange={(e) => setField('height', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
          </Box>
          <Box display="flex" gap={1}>
            <TextField label="Base price" type="number" value={form.basePrice} onChange={(e) => setField('basePrice', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
            {canSeeBuyPrices && (
              <TextField label="Buy price / ct" type="number" value={form.buyPricePerCarat} onChange={(e) => setField('buyPricePerCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
            )}
            <SelectField
              label="Status"
              size="small"
              sx={{ flex: 1 }}
              value={form.status}
              onChange={(next) => setField('status', next)}
              options={DIAMOND_STATUS_OPTIONS}
              emptyLabel=""
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
        <Button onClick={handleAdd} variant="contained" startIcon={<AddIcon />} disabled={loading}>
          {t('inventory.addDiamond')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
